// Fotos reais e organização dos projetos para o layout premium.
// Todas as imagens vêm do acervo do projeto (src/mocks/cases.ts) — nenhuma imagem gerada por IA.
import { cases, type Case } from '@/mocks/cases';

export const LOGO_CINZA =
  '/media/marca/d80a63e2-93d1-4226-9f6e-320a91838de0_horizontal-cinza.png';

export const FOTO_EDNILSON =
  '/media/projetos/32d71f0f-7a31b7ee-30ea-4b20-9815-7ca52e3a7f9d.webp';

/** Versão redimensionada (WebP) de uma foto do acervo. */
export function img(src: string, width: number): string {
  if (!src || !src.includes('storage.readdy-site.link')) return src;
  const sep = src.includes('?') ? '&' : '?';
  return `${src}${sep}w=${width}&q=78&fm=webp`;
}

/** Foto de um projeto pelo id do case e índice da foto. */
export function foto(caseId: number, index = 0): string {
  const c = cases.find((x) => x.id === caseId);
  if (!c || c.images.length === 0) return '';
  return c.images[Math.min(index, c.images.length - 1)];
}

export type Categoria = 'Institucional' | 'Corporativo' | 'Saúde' | 'Hotelaria e varejo' | 'Igrejas';

export const CATEGORIAS: Categoria[] = ['Institucional', 'Corporativo', 'Saúde', 'Hotelaria e varejo', 'Igrejas'];

interface ProjetoMeta {
  id: number;
  nome: string;
  categoria: Categoria;
  resumo: string;
  capa?: number;
}

// Ordem de exibição (mais fortes primeiro) + textos curtos.
const META: ProjetoMeta[] = [
  { id: 9, nome: 'Copergás', categoria: 'Corporativo', resumo: 'Sede administrativa: estações de trabalho, salas de reunião e auditório.', capa: 2 },
  { id: 17, nome: 'Gabinete do Prefeito', categoria: 'Institucional', resumo: 'Mesa de reunião institucional e cadeiras executivas de alto padrão.' },
  { id: 15, nome: 'Câmara de Vertentes', categoria: 'Institucional', resumo: 'Plenário completo, do mobiliário legislativo às cadeiras.', capa: 1 },
  { id: 8, nome: 'Câmara de Ouricuri', categoria: 'Institucional', resumo: 'Obra completa: plenário, auditório tipo cinema e salas.' },
  { id: 12, nome: 'Sicoob', categoria: 'Corporativo', resumo: 'Agência bancária: atendimento, autoatendimento e áreas internas.', capa: 1 },
  { id: 20, nome: 'Sala de Reuniões Especiais', categoria: 'Institucional', resumo: 'Prefeitura de Caruaru. Mesas executivas e cadeiras em couro.' },
  { id: 1, nome: 'Statregi Contabilidade', categoria: 'Corporativo', resumo: 'Escritório completo, do atendimento às salas administrativas.', capa: 2 },
  { id: 4, nome: 'Câmara de Santa Cruz do Capibaribe', categoria: 'Institucional', resumo: 'Plenário e mobiliário institucional do legislativo municipal.' },
  { id: 3, nome: 'Escritório de Advocacia', categoria: 'Corporativo', resumo: 'Salas de atendimento, diretoria e área administrativa.', capa: 2 },
  { id: 22, nome: 'RodoNorte', categoria: 'Corporativo', resumo: 'Sala de reuniões para as decisões estratégicas da empresa.' },
  { id: 13, nome: 'Gabinete Especial', categoria: 'Institucional', resumo: 'Prefeitura de Caruaru. Mesas, estantes e mobiliário executivo.' },
  { id: 6, nome: 'Epic Tower', categoria: 'Corporativo', resumo: 'Recepção corporativa com marcenaria sob medida.', capa: 2 },
  { id: 7, nome: 'Instituto Vida', categoria: 'Saúde', resumo: 'Salas de atendimento, reunião e áreas administrativas.' },
  { id: 16, nome: 'Prefeitura de Xexéu', categoria: 'Institucional', resumo: 'Gabinete com mesas executivas e mobiliário planejado.', capa: 2 },
  { id: 10, nome: 'Secretaria de Finanças de Paulista', categoria: 'Institucional', resumo: 'Atendimento ao público e área administrativa.' },
  { id: 11, nome: 'Prefeitura de Água Preta', categoria: 'Institucional', resumo: 'Setores administrativos e de atendimento, em planejados.', capa: 2 },
  { id: 14, nome: 'Posto de Saúde de Petrolina', categoria: 'Saúde', resumo: 'Recepção, espera, consultórios e áreas administrativas.' },
  { id: 2, nome: 'Clínica em Bonito', categoria: 'Saúde', resumo: 'Sala de espera, recepção e consultórios.' },
  { id: 21, nome: 'Hotel Ibis', categoria: 'Hotelaria e varejo', resumo: 'Espaço de refeições e áreas de convivência do hotel.' },
  { id: 5, nome: 'Caruaru Shopping', categoria: 'Hotelaria e varejo', resumo: 'Mobiliário para a praça de alimentação.', capa: 1 },
  { id: 18, nome: 'Assembleia de Deus', categoria: 'Igrejas', resumo: 'Salão de cultos e eventos com cadeiras empilháveis.' },
];

export interface Projeto {
  id: number;
  nome: string;
  cidade: string;
  categoria: Categoria;
  resumo: string;
  capa: string;
  fotos: string[];
  alt: string;
}

function cidadeDe(c: Case): string {
  return c.location.replace(' - ', ', ');
}

export const PROJETOS: Projeto[] = META.flatMap((m) => {
  const c = cases.find((x) => x.id === m.id);
  if (!c || c.images.length === 0) return [];
  const capaIdx = Math.min(m.capa ?? c.coverIndex ?? 0, c.images.length - 1);
  // A capa abre a galeria.
  const fotos = [c.images[capaIdx], ...c.images.filter((_, i) => i !== capaIdx)];
  return [
    {
      id: c.id,
      nome: m.nome,
      cidade: cidadeDe(c),
      categoria: m.categoria,
      resumo: m.resumo,
      capa: c.images[capaIdx],
      fotos,
      alt: `Projeto de mobiliário da HomeOffice Premium para ${c.client} — ${c.location}`,
    },
  ];
});

export function projeto(id: number): Projeto | undefined {
  return PROJETOS.find((p) => p.id === id);
}
